import { currentPushes, loopFacts, loopVerdict } from './verdicts'
import { Duration, Node, Panel, Pipe, useVertical, withAccount } from './parts'
import { LOOP_HINTS as HINT } from './hints'
import { fmtSeconds, fmtCount } from '../StreamIngest/vocabulary'
import { QUOTE_PUSH_SKIPS, fmtShare, share } from '../Loop/vocabulary'

// The loop's row, quotes to people (README.md, "Stages", says what each stage
// reads and how its status is decided): the Overview draws it under the
// pipeline's, and the Loop page heads its tabs with it. Its popups are
// `hints.js`'s.
//
// `reads` is `useLoopReads`'s answer: the delivery (Mongo), what the event
// table says the pushes led to (BigQuery, read apart and later), and the
// latency answer's product lags. `fleetOnly` is a page narrowed to one
// account, which the loop's reads cannot follow; `user` is that account, which
// the row's links carry so the next page keeps it. `summarized` and `to` are
// as for the pipeline's row.

const LoopPanel = ({
  reads,
  fleetOnly = false,
  user = null,
  summarized = false,
  to = '/loop',
}) => {
  const vertical = useVertical()
  // The window the numbers were read for, not the one being read.
  const { delivery, pushesError, product, shownDays: windowDays } = reads
  // `1d` is today so far (UTC), so its totals read as today's.
  const windowLabel = windowDays === 1 ? 'today' : `last ${windowDays}d`
  // While the event table is read, the stages past the push say so rather
  // than show the last window's.
  const current = currentPushes(reads)
  const read = Boolean(delivery) && !fleetOnly

  // ---- the loop's first stage: what was pushed ----------------------------
  // Step 4b's push outcomes, decided by the pass: only a failed send is loss;
  // a skip is policy and hangs grey. Step 6's friend pushes beside them, as
  // their held rows stand now. Their rows are the Loop page's Pushes tab.
  const facts = loopFacts({ delivery, pushes: current })
  const {
    quotes,
    pushed,
    friendQuotesSent,
    commentsSent,
    requestsSent,
    acceptedSent,
    sent,
    failed,
    lost,
    dropped,
    pastDue,
  } = facts
  const pushSkips = QUOTE_PUSH_SKIPS.filter(
    ({ key }) => (quotes[key] || 0) > 0
  ).map(({ key, label }) => `${fmtCount(quotes[key])} ${label}`)
  const pushPipe = !read
    ? 'pending'
    : failed > 0 || lost > 0
    ? 'error'
    : sent > 0
    ? 'ok'
    : 'idle'
  const pushedStatus = !read
    ? 'pending'
    : pastDue > 0
    ? 'warning'
    : sent > 0
    ? 'ok'
    : 'idle'
  // The product half's lags over the window, as the stages' counts are; an
  // API from before the window's lags were sent falls back to the newest day.
  const productLags = product?.window || product?.days?.[0] || null
  const fetchedInWindow = (product?.days || []).reduce(
    (sum, row) => sum + (row.push_to_fetch?.n || 0),
    0
  )
  const momentToPush = productLags?.moment_to_push?.p50_s ?? null

  // ---- the loop past the push: the event table ---------------------------
  // Summed across kinds. A read the table refused names its reason on the
  // stages it leaves unbuilt.
  // `reached` is the stages' denominator, pushes to a phone that reports:
  // without one, they say so in words rather than "0 of 0".
  const outcomes = read ? facts.outcomes : null
  const reached = outcomes && outcomes.counted > 0 ? outcomes : null
  const unread = fleetOnly
    ? 'fleet only'
    : pushesError
    ? `unread: ${pushesError}`
    : !outcomes
    ? 'loading…'
    : !reached
    ? `no push to a phone that reports ${windowLabel}`
    : null
  const visitedStatus = !outcomes
    ? 'pending'
    : outcomes.visited > 0
    ? 'ok'
    : 'idle'
  const heardStatus = !outcomes ? 'pending' : outcomes.heard > 0 ? 'ok' : 'idle'

  // ---- the loop's last stage: a quote reached a friend --------------------
  // In friendships with a phone that reports on at least one side (the event
  // table's Friends read), whether either friend heard the other's quotes.
  // An API from before the read counted friendships carries none.
  const friendsSummary =
    read && !reads.friendsLoading ? reads.friends?.summary : null
  const friendships =
    friendsSummary?.friendships != null ? friendsSummary : null
  const friendsUnread = fleetOnly
    ? 'fleet only'
    : reads.friendsError
    ? `unread: ${reads.friendsError}`
    : !friendships
    ? 'loading…'
    : friendships.friendships === 0
    ? 'no friendships yet'
    : null
  const friendsStatus = !friendships
    ? 'pending'
    : friendships.friendships_heard > 0
    ? 'ok'
    : 'idle'

  const verdict = loopVerdict(reads, { fleetOnly })
  const leads = !(vertical && summarized)

  return (
    <Panel
      title="Loop · quotes → people"
      to={withAccount(to, user)}
      status={verdict.status}
      verdict={leads ? verdict : null}
    >
      <Pipe
        status={pushPipe}
        label={
          pushPipe === 'pending' ? (
            'step 4b'
          ) : momentToPush == null ? (
            'no quote pushed'
          ) : (
            <Duration>{fmtSeconds(momentToPush)}</Duration>
          )
        }
        hint={HINT.pushPipe}
        leak={
          [
            failed > 0
              ? `${fmtCount(failed)} push${failed === 1 ? '' : 'es'} failed`
              : null,
            lost > 0 ? `${fmtCount(lost)} lost` : null,
          ]
            .filter(Boolean)
            .join(' · ') || null
        }
      />
      <Node
        icon="🔔"
        title="Pushed"
        status={pushedStatus}
        value={read ? fmtCount(sent) : '—'}
        lines={
          read
            ? [
                `sent ${windowLabel}`,
                `${fmtCount(pushed)} own quote · ${fmtCount(
                  friendQuotesSent
                )} friends' quotes`,
                [
                  `${fmtCount(commentsSent)} comment`,
                  requestsSent
                    ? `${fmtCount(requestsSent)} friend request`
                    : null,
                  acceptedSent ? `${fmtCount(acceptedSent)} accepted` : null,
                ]
                  .filter(Boolean)
                  .join(' · '),
                pushed > 0
                  ? `${fmtCount(
                      fetchedInWindow
                    )} fetched · push → phone ${fmtSeconds(
                      productLags?.push_to_fetch?.p50_s ?? null
                    )}`
                  : `no quote of theirs pushed ${windowLabel}`,
              ]
            : ['a push about each quote']
        }
        diversions={[
          pushSkips.length > 0
            ? {
                label: 'not pushed, by design',
                lines: pushSkips,
                hint: HINT.notPushed,
              }
            : null,
          dropped > 0
            ? {
                label: `friend pushes dropped · ${fmtCount(dropped)}`,
                lines: ['the recheck found nothing to say'],
                hint: HINT.friendsDropped,
              }
            : null,
        ]}
        alert={
          pastDue > 0
            ? {
                tone: 'warning',
                text: `${fmtCount(pastDue)} friend push${
                  pastDue === 1 ? '' : 'es'
                } past due`,
              }
            : null
        }
        hint={HINT.pushed}
        link={{
          to: withAccount('/loop?tab=pushes#delivery', user),
          label: 'how they were delivered',
        }}
      />
      <Pipe
        status={visitedStatus}
        label={
          reached ? fmtShare(share(reached.visited, reached.counted)) : 'tap'
        }
        hint={HINT.tapPipe}
      />
      <Node
        icon="👆"
        title="Visited"
        status={visitedStatus}
        value={reached ? fmtCount(reached.visited) : '—'}
        lines={
          reached
            ? [
                `of ${fmtCount(
                  reached.counted
                )} pushes of every kind to a reporting phone`,
                'a floor on the taps',
              ]
            : [unread || 'a push opened the app']
        }
        hint={HINT.visited}
        link={{
          to: withAccount('/loop?tab=pushes', user),
          label: 'what each push led to',
        }}
      />
      <Pipe
        status={heardStatus}
        label={
          reached?.visited
            ? fmtShare(share(reached.heard, reached.visited))
            : 'play'
        }
        hint={HINT.playPipe}
      />
      <Node
        icon="🎧"
        title="Heard"
        status={heardStatus}
        value={reached ? fmtCount(reached.heard) : '—'}
        lines={
          reached
            ? [
                `${fmtShare(
                  share(reached.heard, reached.counted)
                )} of pushes led to a listen`,
                `${fmtCount(reached.about)} to what the push was about`,
              ]
            : [unread || 'somebody heard a quote']
        }
        hint={HINT.heard}
        link={{
          to: withAccount('/loop?tab=listens', user),
          label: 'how people listen',
        }}
      />
      <Pipe
        status={friendsStatus}
        label={
          friendships?.friendships
            ? fmtShare(
                share(friendships.friendships_heard, friendships.friendships)
              )
            : 'share'
        }
        hint={HINT.sharePipe}
      />
      <Node
        icon="👥"
        title="Friends"
        status={friendsStatus}
        value={friendships ? fmtCount(friendships.friendships_heard) : '—'}
        lines={
          friendsUnread
            ? [friendsUnread]
            : [
                `of ${fmtCount(friendships.friendships)} friendship${
                  friendships.friendships === 1 ? '' : 's'
                } had a friend listen`,
                `${fmtCount(friendships.friendships_both)} listened both ways`,
                `${fmtCount(friendships.befriended)} new friendship${
                  friendships.befriended === 1 ? '' : 's'
                } ${windowLabel}`,
              ]
        }
        hint={HINT.friends}
        link={{
          to: withAccount('/loop?tab=friends#network', user),
          label: 'who hears whom',
        }}
      />
    </Panel>
  )
}

export default LoopPanel
